import { useEffect, useState } from "react";
import { fetchContexts, createContext, editContext } from "./api";

const ContextViewer = () => {
  const [contexts, setContexts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentContext, setCurrentContext] = useState({
    name: '',
    description: '',
    scope: 'CDP',
    properties: {},
    requiresCallBack: false,
    filters: []
  });
  const [newProperty, setNewProperty] = useState({ key: '', value: '' });

  useEffect(() => {
    loadContexts();
  }, []);

  const loadContexts = async () => {
    try {
      const data = await fetchContexts();
      setContexts(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('Error loading contexts:', error);
      setContexts([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await editContext(currentContext.source.itemId, currentContext);
      } else {
        await createContext(currentContext);
      }
      await loadContexts();
      handleCloseForm();
    } catch (error) {
      console.error('Error saving context:', error);
    }
  };

  const handleEdit = (context) => {
    setCurrentContext({
      name: context.source.name,
      description: context.source.description,
      scope: context.source.scope,
      properties: context.properties || {},
      requiresCallBack: context.requiresCallBack || false,
      filters: context.filters || [],
      source: context.source
    });
    setIsEditing(true);
    setShowForm(true);
  };

  const handleAddProperty = () => {
    if (newProperty.key && newProperty.value) {
      setCurrentContext({
        ...currentContext,
        properties: {
          ...currentContext.properties,
          [newProperty.key]: newProperty.value
        }
      });
      setNewProperty({ key: '', value: '' });
    }
  };

  const handleRemoveProperty = (key) => {
    const newProperties = { ...currentContext.properties };
    delete newProperties[key];
    setCurrentContext({
      ...currentContext,
      properties: newProperties
    });
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setCurrentContext({
      name: '',
      description: '',
      scope: 'CDP',
      properties: {},
      requiresCallBack: false,
      filters: []
    });
    setNewProperty({ key: '', value: '' });
  };

  return (
    <div className="context-container">
      <div className="context-header">
        <h1>Context Manager</h1>
        <button 
          className="create-context-btn"
          onClick={() => {
            setIsEditing(false);
            setShowForm(true);
          }}
        >
          Create New Context
        </button>
      </div>

      {showForm && (
        <div className="create-context-form">
          <h2>{isEditing ? 'Edit Context' : 'Create New Context'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Context Name</label>
              <input
                type="text"
                value={currentContext.name}
                onChange={(e) => setCurrentContext({...currentContext, name: e.target.value})}
                placeholder="Enter context name"
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={currentContext.description}
                onChange={(e) => setCurrentContext({...currentContext, description: e.target.value})}
                placeholder="Enter context description"
              />
            </div>

            <div className="form-group">
              <label>Scope</label>
              <input
                type="text"
                value={currentContext.scope}
                onChange={(e) => setCurrentContext({...currentContext, scope: e.target.value})}
                placeholder="Enter scope"
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={currentContext.requiresCallBack}
                  onChange={(e) => setCurrentContext({...currentContext, requiresCallBack: e.target.checked})}
                />
                Requires Callback
              </label>
            </div>

            <div className="properties-section">
              <h3>Properties</h3>
              <div className="properties-list">
                {Object.entries(currentContext.properties || {}).map(([key, value]) => (
                  <div key={key} className="property-item">
                    <span className="property-label">{key}: {value}</span>
                    <button 
                      type="button"
                      onClick={() => handleRemoveProperty(key)}
                      className="remove-property-btn"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="add-property-form">
                <div className="property-inputs">
                  <input
                    type="text"
                    value={newProperty.key}
                    onChange={(e) => setNewProperty({...newProperty, key: e.target.value})}
                    placeholder="Property key"
                    className="property-input"
                  />
                  <input
                    type="text"
                    value={newProperty.value}
                    onChange={(e) => setNewProperty({...newProperty, value: e.target.value})}
                    placeholder="Property value"
                    className="property-input"
                  />
                </div>
                <button 
                  type="button"
                  onClick={handleAddProperty}
                  className="add-property-btn"
                >
                  Add Property
                </button>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {isEditing ? 'Update Context' : 'Create Context'}
              </button>
              <button 
                type="button" 
                className="cancel-btn"
                onClick={handleCloseForm}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="contexts-list">
        {contexts.map((context, index) => (
          <div key={context.source?.itemId || index} className="context-card">
            <div className="context-info">
              <h3>{context.source?.name || 'Unnamed Context'}</h3>
              <p>{context.source?.description}</p>
            </div>
            <div className="context-properties">
              <h4>Properties</h4>
              {Object.entries(context.properties || {}).map(([key, value]) => (
                <div key={key} className="property-item">
                  <span className="property-key">{key}:</span>
                  <span className="property-value">{value}</span>
                </div>
              ))}
            </div>
            <div className="context-actions">
              <button 
                className="edit-btn"
                onClick={() => handleEdit(context)}
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContextViewer;
